import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  createLocalContentRef,
  getContentPreviewUrl,
  isContentFileImage,
  isContentFilePdf,
  normalizeContentFileFromApi,
} from '../../utils/ReusableFunctions';

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const genId = (i = 0) => `${Date.now()}-${Math.round(Math.random() * 100000)}-${i}`;

const toInternalItems = (input) => {
  if (!input) return [];

  const refs = Array.isArray(input)
    ? normalizeContentFileFromApi(input, true)
    : [normalizeContentFileFromApi(input)];

  return refs.filter(Boolean).map((ref, idx) => ({
    ...ref,
    _id: ref._id || genId(idx),
  }));
};

const toParentRefs = (items) => items.map(({ _id, ...rest }) => rest);

const SortableItem = ({ item, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: 'grab',
    userSelect: 'none',
    display: 'inline-block',
    marginRight: 8,
  };

  const src = getContentPreviewUrl(item) || '';

  if (isContentFileImage(item)) {
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="image-preview">
        <img src={src} alt={item.name || 'Content Image'} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />
        <button type="button" className="pointer" onClick={(e) => onRemove(e, index)}>❌</button>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="pdf-preview d-flex align-items-center">
      <a href={src} target="_blank" rel="noreferrer">
        <div className="primary-btn">View Pdf</div>
      </a>
      <span onClick={(e) => onRemove(e, index)} style={{ marginLeft: 8, cursor: 'pointer' }}>❌</span>
    </div>
  );
};

const CarouselFileUploader = ({
  id,
  isUploadContentFile = [],
  isPdf = false,
  updateContentFile = () => {},
  onRemove = () => {},
  label,
}) => {
  const [images, setImages] = useState(() => toInternalItems(isUploadContentFile));
  const [activeId, setActiveId] = useState(null);
  const [isPdfState, setIsPdfState] = useState(isPdf);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const notifyParent = useCallback(
    (nextImages) => {
      updateContentFile(toParentRefs(nextImages));
    },
    [updateContentFile]
  );

  useEffect(() => {
    setImages(toInternalItems(isUploadContentFile));
    setIsPdfState(isPdf);
  }, [isUploadContentFile, isPdf]);

  const hasPdf = useMemo(
    () => images.some((item) => isContentFilePdf(item)),
    [images]
  );

  const handleFileChange = (event) => {
    const files = Array.from(event.target?.files || []);
    let imageFiles = [...images];

    const existingPdf = imageFiles.some((file) => isContentFilePdf(file));

    if (existingPdf && files.some((file) => file.type?.startsWith('image/'))) {
      toast.warn('Cannot upload images when a PDF is already uploaded.');
      return;
    }

    if (
      imageFiles.some((file) => isContentFileImage(file)) &&
      files.some((file) => file?.type === 'application/pdf')
    ) {
      toast.warn('Cannot upload a PDF when images are already uploaded.');
      return;
    }

    const totalImageCount =
      imageFiles.filter((file) => isContentFileImage(file)).length +
      files.filter((file) => file?.type?.startsWith('image/')).length;

    if (totalImageCount > 10) {
      toast.warn('Cannot upload more than 10 images.');
      return;
    }

    let validImages = [];
    let processedCount = 0;

    files.forEach((file) => {
      if (file.type === 'application/pdf') {
        if (!existingPdf) {
          setIsPdfState(true);
          const nextImages = [...imageFiles, { ...createLocalContentRef(file), _id: genId() }];
          setImages(nextImages);
          notifyParent(nextImages);
        } else {
          toast.warn('Only one PDF is allowed.');
        }
      } else if (file.type.startsWith('image/')) {
        const img = new window.Image();
        const imageUrl = URL.createObjectURL(file);
        img.src = imageUrl;

        img.onload = () => {
          processedCount++;

          if (img.width > 2000 || img.height > 2000) {
            toast.error(`"${file.name}" exceeds 2000x2000 pixels and won't be uploaded.`);
            URL.revokeObjectURL(imageUrl);
          } else {
            validImages.push({ ...createLocalContentRef(file), _id: genId() });
          }

          if (processedCount === files.length) {
            const nextImages = [...imageFiles, ...validImages];
            setImages(nextImages);
            notifyParent(nextImages);
          }
        };

        img.onerror = () => {
          processedCount++;
          URL.revokeObjectURL(imageUrl);
          if (processedCount === files.length) {
            const nextImages = [...imageFiles, ...validImages];
            setImages(nextImages);
            notifyParent(nextImages);
          }
        };
      }
    });
  };

  const removeImage = (e, index) => {
    e?.preventDefault();
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    notifyParent(updatedImages);
    onRemove();
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setImages((items) => {
        const oldIndex = items.findIndex((it) => it._id === active.id);
        const newIndex = items.findIndex((it) => it._id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        notifyParent(newItems);
        return newItems;
      });
    }
  };

  if (!label) {
    label = hasPdf || isPdfState ? 'Accepted file type: pdf' : 'Accepted file types: image, pdf';
  }

  return (
    <div className="img-form-group">
      <label className="form-label">Upload Content</label>

      <div className="img-upload-wrapper">
        <input
          type="file"
          multiple
          id={`carouselUpload${id}`}
          onChange={handleFileChange}
          accept="image/*,application/pdf"
        />
        <label htmlFor={`carouselUpload${id}`}>
          <div className="primary-btn py-1 f-s-10 pointer">Upload</div>
          {label && <p className='inter-regular f-s-8 lh-10 grey-757'>{label}</p>}
        </label>
      </div>

      {images?.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={images.map((i) => i._id)} strategy={verticalListSortingStrategy}>
            <div className="selected-images" style={{ display: 'flex', flexDirection: 'row', marginTop: 12 }}>
              {images.map((img, index) => (
                <SortableItem key={img._id} item={img} index={index} onRemove={removeImage} />
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeId ? (
              (() => {
                const activeItem = images.find((it) => it._id === activeId);
                if (!activeItem) return null;
                const src = getContentPreviewUrl(activeItem) || '';
                if (isContentFileImage(activeItem)) {
                  return <img src={src} alt={activeItem.name || 'dragging'} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />;
                }
                return <div className="primary-btn">View Pdf</div>;
              })()
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default CarouselFileUploader;
